import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { CheckCircle2 } from "lucide-react";

const features = [
  "Specialists in remodels, stucco, tile, and stone masonry",
  "Licensed & Insured for your protection",
  "15+ years of hands-on experience",
  "Flexible scheduling to fit your timeline",
  "Serving Santaquin and surrounding areas",
  "Free quotes with honest, transparent pricing",
  "Bilingual service - English & Spanish"
];

export default function AboutSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="about"
      className="py-24 md:py-32 bg-[#F5F5F4]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Typography */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57534E] mb-4 block">
              Our Philosophy
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-[#1C1917] mb-8 leading-[1.1]">
              We sell our service,
              <br />
              <span className="text-[#166534]">not the website.</span>
            </h2>
            <p className="text-[#57534E] text-lg leading-relaxed mb-8">
              At JV Home Renovation, we believe in doing things right the first time. 
              With years of hands-on experience across every aspect of construction, 
              we bring the expertise and dedication your project deserves.
            </p>
            <p className="text-[#57534E] leading-relaxed mb-10">
              Quality construction takes time, and we understand that. That's why our 
              scheduling is flexible - we work with you to ensure every detail is 
              perfect, not rushed.
            </p>
            
            <ul className="space-y-4">
              {features.map((feature, index) => (
                <motion.li
                  key={feature}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-3 text-[#1C1917]"
                >
                  <CheckCircle2 className="text-[#166534] flex-shrink-0" size={20} />
                  <span>{feature}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative"
          >
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/347pn02n_Untitled%20design%20%2814%29.png"
                alt="Staircase and drywall installation by JV Home Renovation"
                className="w-full h-[500px] object-cover"
                data-testid="about-image"
              />
              {/* Accent border */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#166534] -z-10" />
            </div>
            
            {/* Stats overlay */}
            <div className="absolute -left-8 bottom-8 bg-[#1C1917] text-white p-6">
              <p className="font-['Outfit'] text-4xl font-medium mb-1">100%</p>
              <p className="text-white/70 text-sm">Commitment to Quality</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
